import { createRouter, createWebHistory } from 'vue-router';

import { routes } from '../router/routes';
import { useTitle } from '@vueuse/core';
import { captureException, captureMessage } from '@sentry/vue';
import { getSanitisedHazardType, HazardTypeToRouteTitleMap } from '@/features';
import { RouteNames } from './types';

export const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed')) {
    if (!to?.fullPath) return window.location.reload();

    window.location.href = to.fullPath;
    return;
  }

  captureException(error);
});

export const pageTitleHandler = useTitle(undefined, { titleTemplate: '%s - EmergencyWA Warnings & Incidents' });

const defaultPageDescription =
  "Emergency WA is the State's official website for community warnings and other emergency management information for bushfires, storms, cyclones, floods and other hazards.";

router.beforeEach((to, _, next) => {
  const { hazardId, hazardType } = to.params;

  // Addresses a weird issue where there is a double navigation to the same route
  // which causes the hazard type to be set as the hazard ID
  if (hazardId && hazardType && hazardId === hazardType) {
    captureMessage('Route navigation to the same route with the same params', {});
    return;
  }

  const sanitisedHazardType = getSanitisedHazardType(hazardType)
    ? HazardTypeToRouteTitleMap[getSanitisedHazardType(hazardType)!]
    : undefined;

  if (to.name === RouteNames.Hazards) {
    useTitle(to.meta?.title as string);
  } else {
    pageTitleHandler.value = sanitisedHazardType ?? (to?.meta?.title as string) ?? 'Unknown Page';
  }

  const descriptionElement = document.createElement('meta');
  descriptionElement.name = 'description';

  const description = (to.meta.description as string) ?? defaultPageDescription;

  descriptionElement.content = description;

  document.head.appendChild(descriptionElement);

  next();
});

export default router;

export * from './types';
